import { useNotificationSettings } from '../../hooks/useNotificationSettings';
import { useNotifications } from '../../hooks/useNotifications';
import { useNotificationHistory } from '../../hooks/useNotificationHistory';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';

export default function NotificationSettings() {
  const navigate = useNavigate();
  const {
    settings: notifications,
    updateSetting,
    isLoading,
    error,
    requestPermission,
    showTestNotification
  } = useNotificationSettings();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { addNotification, simulateNotifications } = useNotificationHistory();
  const { isAdmin } = useAdmin();

  const handleNotificationChange = async (key: keyof typeof notifications, value: boolean) => {
    try {
      await updateSetting(key, value);
      showSuccess(
        'Notification Setting Updated',
        `${key} notifications are now ${value ? 'enabled' : 'disabled'}`
      );
    } catch (error) {
      showError(
        'Update Failed',
        'Failed to update notification setting. Please try again.'
      );
    }
  };

  const handleRequestPermission = async () => {
    try {
      const granted = await requestPermission();
      if (granted) {
        showSuccess('Permission Granted', 'Browser notifications are now enabled!');
      } else {
        showError('Permission Denied', 'Browser notifications were not enabled. You can enable them in your browser settings.');
      }
    } catch (error) {
      showError('Permission Error', 'Failed to request notification permission');
    }
  };

  const handleTestNotification = () => {
    const success = showTestNotification();
    if (success) {
      showInfo('Test Notification', 'Check if you received a browser notification!');
    } else {
      showError('Test Failed', 'Unable to show browser notification. Make sure permissions are enabled.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 theme-surface">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/settings')}
            className="theme-text-secondary hover:theme-text-primary p-2 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold theme-text">Notifications</h1>
        </div>
        <button
          onClick={() => navigate('/')}
          className="theme-text-secondary hover:theme-text-primary p-2 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.334 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Browser Notifications Permission - Only show if permission not granted */}
      {typeof Notification !== 'undefined' && Notification.permission !== 'granted' && (
        <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100">Browser Notifications</h3>
              <p className="text-sm text-blue-700 dark:text-blue-200">Enable browser notifications to receive alerts even when the app is closed</p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleRequestPermission}
                disabled={isLoading}
                className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                Enable
              </button>
              <button
                onClick={handleTestNotification}
                className="px-3 py-1 bg-healthy-green text-white text-sm font-medium rounded hover:bg-green-700 transition-colors"
              >
                Test
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {/* Notifications Section */}
        <div>
          <h2 className="text-lg font-semibold theme-text-primary mb-4">Notification Preferences</h2>
          <div className="theme-surface border rounded-lg overflow-hidden">
            <SettingsItem
              title="Community Posts"
              type="switch"
              isEnabled={notifications.communityPosts}
              isLoading={isLoading}
              onToggle={(value) => {
                handleNotificationChange('communityPosts', value);
              }}
            />
            <SettingsItem
              title="Upvotes"
              type="switch"
              isEnabled={notifications.upvotes}
              isLoading={isLoading}
              onToggle={(value) => {
                handleNotificationChange('upvotes', value);
              }}
            />
            <SettingsItem
              title="Comments"
              type="switch"
              isEnabled={notifications.comments}
              isLoading={isLoading}
              onToggle={(value) => {
                handleNotificationChange('comments', value);
              }}
            />
            <SettingsItem
              title="Poll Votes"
              type="switch"
              isEnabled={notifications.pollVotes}
              isLoading={isLoading}
              onToggle={(value) => {
                handleNotificationChange('pollVotes', value);
              }}
            />
          </div>
        </div>

        {/* Demo Notifications Section - Admin Only */}
        {isAdmin && (
        <div>
          <h2 className="text-lg font-semibold theme-text-primary mb-4">Demo Notifications (Admin Only)</h2>
          <div className="theme-surface border rounded-lg overflow-hidden">
            <div className="p-4">
              <p className="text-sm theme-text-secondary mb-4">
                Try out different types of notifications to see how they work:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => addNotification({
                    type: 'upvote',
                    title: 'New Upvote!',
                    message: 'Dr. Smith upvoted your post about shoulder surgery',
                    actor: { id: 123, name: 'Dr. Smith', avatar: 'https://i.pravatar.cc/40?u=smith' },
                    actionData: { postId: 341 }
                  })}
                  className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                >
                  Upvote Notification
                </button>
                <button
                  onClick={() => addNotification({
                    type: 'comment',
                    title: 'New Comment',
                    message: 'Dr. Johnson commented on your case study',
                    actor: { id: 456, name: 'Dr. Johnson', avatar: 'https://i.pravatar.cc/40?u=johnson' },
                    actionData: { postId: 341, commentId: 789 }
                  })}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Comment Notification
                </button>
                <button
                  onClick={() => addNotification({
                    type: 'poll_vote',
                    title: 'Poll Vote',
                    message: 'Someone voted on your poll about treatment options',
                    actionData: { postId: 567 }
                  })}
                  className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Poll Vote Notification
                </button>
                <button
                  onClick={() => addNotification({
                    type: 'community_post',
                    title: 'Community Post',
                    message: 'New post in your specialty: Orthopedic Surgery',
                    actionData: { postId: 890 }
                  })}
                  className="px-4 py-2 bg-healthy-green text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                >
                  Community Post
                </button>
              </div>
              <div className="mt-4">
                <button
                  onClick={simulateNotifications}
                  className="w-full px-4 py-2 theme-bg-tertiary theme-text-primary text-sm font-medium rounded-lg hover:bg-opacity-80 transition-colors"
                >
                  Simulate Multiple Notifications (Demo)
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

interface SettingsItemProps {
  title: string;
  type: 'switch';
  isEnabled?: boolean;
  onToggle?: (value: boolean) => void;
  isLoading?: boolean;
}

function SettingsItem({
  title,
  type,
  isEnabled = false,
  onToggle,
  isLoading = false
}: SettingsItemProps) {
  return (
    <div className="px-4 py-3 border-b theme-border-primary last:border-b-0">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium theme-text-primary">
          {title}
        </span>

        {type === 'switch' && (
          <button
            onClick={() => {
              if (typeof onToggle === 'function') {
                onToggle(!isEnabled);
              }
            }}
            disabled={isLoading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-healthy-green focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              isEnabled ? 'bg-healthy-green' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform flex items-center justify-center ${
                isEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            >
              {isLoading && (
                <div className="w-2 h-2 border theme-border-secondary border-t-theme-text-secondary rounded-full animate-spin"></div>
              )}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
